import { Component, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LanguageService } from 'src/app/services/language.service';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { Baseline, Question } from 'src/app/shared/models/questionnaire.model';

@Component({
    selector: 'app-questionnaire-baselines',
    templateUrl: './questionnaire-baselines.component.html',
    styleUrl: './questionnaire-baselines.component.scss',
    standalone: false
})
export class QuestionnaireBaselinesComponent {
	@ViewChild('deleteBaselineModal') deleteBaselineModal!: TemplateRef<any>;
	@ViewChild('viewBaselineModal') viewBaselineModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;

	listStatus: any = {
		DRAFT: 'info',
		ACTIVE: 'success',
		PAUSED: 'warning',
		CANCELED: 'danger'
	}

	submitted = false;
	spinner = false;
	success = false;
	successMessage = '';
	message = '';

	questionnaireBaselines: any[] = [];
	currentPage: number = 1;
  pageSize: number = 8;
  total: number = 0;
  searchText: string = '';
  sortColumn: string = 'first_name';
  sortDirection: string = 'desc';

	startIndex: number = 0;
  endIndex: number = 0;

	currentUser: any = null;
	loading = true;

	selectedBaseline!: Baseline;
	deleteBaselineModalRef: any;

	showDesc = false;
	showQuestion = false;
	showNext = true;

	chapterIndex = 0;
	questionIndex = 0;

	currentViewQuestion!: Question;

	private searchSubject = new Subject<string>();

	constructor(private questionnaireService: QuestionnaireService, private authService: AuthService, private modalService: NgbModal, public translate: TranslateService) {}

	ngOnInit(): void {
		this.currentUser = this.authService.currentUserValue;
		this.getQuestionnaireBaselines();

    // Subscribe to search input changes with debounce
    this.searchSubject.pipe(
      debounceTime(300), // Adjust debounce time as needed
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1; // Reset to first page when search text changes
      this.getQuestionnaireBaselines();
    });
	}

	getQuestionnaireBaselines() {
		this.loading = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };

		this.questionnaireService.getClientQuestionnaireBaselines(params).subscribe({
			next: (response: any) => {
				this.questionnaireBaselines = response.questionnaireBaselines;
				this.total = response.total;
				this.calculateIndexes();
				this.loading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Questionnaire Baselines');
			}
		});
  }

	calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.total);
  }

	onSearch() {
    this.searchSubject.next(this.searchText);
  }

	onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
		
    this.getQuestionnaireBaselines();
  }

	refreshQuestionnaireBaselines() {
		this.currentPage = 1;
		this.searchText = '';
		this.getQuestionnaireBaselines();
	}

	openDeleteQuestionModal(baseline: Baseline) {
		this.selectedBaseline = baseline;

		this.deleteBaselineModalRef = this.modalService.open(this.deleteBaselineModal, { size: 'l', backdrop: 'static', keyboard : false });
	}

	deleteBaseline() {
		this.questionnaireService.deleteQuestionnaireBaseline(this.selectedBaseline.id).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.success = true;
				this.deleteBaselineModalRef.close();
				this.successMessage = 'Baseline deleted successfully.';

				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.submitted = false;
				this.spinner = false;
				this.success = false;
				this.deleteBaselineModalRef.close();
				this.successMessage = 'Baseline can not be deleted!';
				this.message = err.message ? err.message : err;

				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			}
		});
	}

	closeModal() {
		if (this.success) {
			this.refreshQuestionnaireBaselines();
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

	openViewBaselineModal(baseline_id: number) {
		this.questionIndex = 0;
		this.showQuestion = false;
		
		this.questionnaireService.getClientQuestionnaireBaseline(baseline_id).subscribe({
			next: (data: Baseline) => {
				this.selectedBaseline = data;
				this.translate.use(data.lang_code);
				this.showDesc = true;
				this.modalService.open(this.viewBaselineModal, { size: 'xl', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting baseline');
			}
		});
	}

	backQuestion() {
		this.showNext = true;

		if (this.questionIndex === 0) {
			this.showQuestion = false;
			this.showDesc = true;
		} else {
			this.questionIndex--;
			this.currentViewQuestion = this.selectedBaseline.baseline_questions[this.questionIndex];
		}
	}

	nextQuestion() {
		if (this.questionIndex === 0 && this.showDesc) {
			this.showDesc = false;
			this.showQuestion = true;
		} else {
			this.questionIndex++;
		}

		if (this.questionIndex === this.selectedBaseline.baseline_questions.length-1) {
			this.showNext = false;
		}

		this.currentViewQuestion = this.selectedBaseline.baseline_questions[this.questionIndex];
	}
}
